import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import { Spacer } from '@kizen/kds/Spacer';
import { useMutation } from '@tanstack/react-query';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Button } from '@kizen/kds/Button';
import BillingService from 'services/BillingService';
import {
  BillingSummary,
  BillingUpdateSettingsPayload,
} from '@kizen/api/billing';
import Switch from '__components/Kizen/Switch';
import Input from '__components/Kizen/Input';
import { INPUT_TYPES } from '__components/Kizen/Input';
import { toastDelay, toastVariant, useToast } from '__components/ToastProvider';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { Icon } from '@kizen/kds/Icon';

type OverageBudgetBlockProps = {
  usageSummary?: BillingSummary;
  refetch: () => void;
  disabled?: boolean;
  readOnly?: boolean;
};

type BudgetKey =
  | 'overall_overage_budget_limit'
  | 'ai_credits_overage_budget_limit'
  | 'automation_overage_budget_limit'
  | 'email_overage_budget_limit'
  | 'ai_passthrough_overage_budget_limit';

const BUDGET_KEYS: BudgetKey[] = [
  'overall_overage_budget_limit',
  'ai_credits_overage_budget_limit',
  'automation_overage_budget_limit',
  'email_overage_budget_limit',
  'ai_passthrough_overage_budget_limit',
];

// A null value means the category is unlimited (the backend default for a
// budget nobody has set)
type BudgetValues = Record<BudgetKey, string | null>;

const enforcePositive = (value: number) => {
  return value < 0 ? 0 : value;
};

// Builds a record with one value per budget key. Listing the keys keeps the
// result fully typed, so a new budget key fails to compile until it's added.
const mapBudgetKeys = <V,>(
  getValue: (key: BudgetKey) => V
): Record<BudgetKey, V> => ({
  overall_overage_budget_limit: getValue('overall_overage_budget_limit'),
  ai_credits_overage_budget_limit: getValue('ai_credits_overage_budget_limit'),
  automation_overage_budget_limit: getValue('automation_overage_budget_limit'),
  email_overage_budget_limit: getValue('email_overage_budget_limit'),
  ai_passthrough_overage_budget_limit: getValue(
    'ai_passthrough_overage_budget_limit'
  ),
});

const buildPayload = (value: string | null): BillingUpdateSettingsPayload =>
  mapBudgetKeys(() => value);

const noOveragePayload = buildPayload(String(0));
const unlimitedPayload = buildPayload(null);

// An undefined saved amount means the limit is null (unlimited); a saved $0
// stays "0".
const toInitialValue = (amount?: number) => amount?.toString() ?? null;

// Null stays null (unlimited); anything else saves as a non-negative amount.
const toPayloadValue = (value: string | null) =>
  value === null ? null : String(enforcePositive(Number(value) || 0));

// Every row shares one four-column grid so all budget fields are the same width.
const GRID_CLASS_NAME = 'grid grid-cols-4 gap-20';

const TooltipIcon = ({ tooltip }: { tooltip: string }) => (
  <Icon
    size="sm"
    icon="message-info-circle"
    tooltipEnabled
    tooltipTitleOverride=""
    tooltipDescriptionOverride={tooltip}
    tooltipPosition="top"
  />
);

const LabelWithTooltip = ({
  label,
  tooltip,
}: {
  label: string;
  tooltip: string;
}) => (
  <>
    {label}
    <span className="ml-[5px] inline-flex align-middle">
      <TooltipIcon tooltip={tooltip} />
    </span>
  </>
);

const BudgetField = ({
  label,
  value,
  onChange,
  disabled,
  qaId,
  helperText,
}: {
  label: ReactNode;
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  qaId: string;
  helperText?: string | null;
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <Input
        label={label}
        variant="outline"
        onChange={(val: string) => onChange(val || '0')}
        value={value ?? ''}
        placeholder={t('Unlimited')}
        type={INPUT_TYPES.PRICE}
        disabled={disabled}
        data-qa-id={qaId}
      />
      {helperText ? (
        <>
          <Spacer size={5} />
          <Typography size="sm" fontStyle="italic" color="font-placeholder">
            {helperText}
          </Typography>
        </>
      ) : null}
    </div>
  );
};

export const OverageBudgetsBlock = (props: OverageBudgetBlockProps) => {
  const { usageSummary, refetch, disabled, readOnly } = props;

  const { t } = useTranslation();
  const [showToast, _, clearToasts] = useToast();

  // Currently Saved Values
  const currentBudgets = useMemo<Record<BudgetKey, number | undefined>>(
    () => ({
      overall_overage_budget_limit:
        usageSummary?.core_platform?.budget_limit?.amount,
      ai_credits_overage_budget_limit:
        usageSummary?.core_platform?.ai_credits?.budget_limit?.amount,
      automation_overage_budget_limit:
        usageSummary?.automations?.budget_limit?.amount,
      email_overage_budget_limit: usageSummary?.emails?.budget_limit?.amount,
      ai_passthrough_overage_budget_limit:
        usageSummary?.core_platform?.passthrough?.budget_limit?.amount,
    }),
    [usageSummary]
  );

  const currentIsUnlimited =
    usageSummary?.core_platform?.budget_limit === null &&
    usageSummary?.core_platform?.ai_credits?.budget_limit === null &&
    usageSummary?.automations?.budget_limit === null &&
    usageSummary?.emails?.budget_limit === null &&
    usageSummary?.core_platform?.passthrough?.budget_limit === null;
  const currentIsNoOverage =
    usageSummary?.core_platform?.budget_limit?.amount === 0;
  const currentAllowsOverage = !currentIsNoOverage;

  const [allowOverage, setAllowOverage] = useState(currentAllowsOverage);
  const [allowUnlimitedOverage, setAllowUnlimitedOverage] =
    useState(currentIsUnlimited);
  const [budgets, setBudgets] = useState<BudgetValues>(() =>
    mapBudgetKeys((key) => toInitialValue(currentBudgets[key]))
  );

  const setBudget = useCallback((key: BudgetKey, value: string) => {
    setBudgets((prev) => ({ ...prev, [key]: value }));
  }, []);

  const canAllowOverage = allowOverage;
  const canHaveUnlimitedBudget = canAllowOverage && allowUnlimitedOverage;
  const showBreakdown = allowOverage && !allowUnlimitedOverage;

  const setDefaultBudgets = () => {
    setBudgets(mapBudgetKeys(() => '0'));
  };

  const toggleAllowOverage = (enabled: boolean) => {
    setAllowOverage(enabled);

    if (enabled) {
      setDefaultBudgets();
    }
  };

  const toggleUnlimitedBudget = (enabled: boolean) => {
    setAllowUnlimitedOverage(enabled);

    if (enabled) {
      toggleAllowOverage(true);
    } else {
      setDefaultBudgets();
    }
  };

  if (!canHaveUnlimitedBudget && allowUnlimitedOverage) {
    toggleUnlimitedBudget(false);
  }

  const updateBillingSettingsMutation = useMutation({
    mutationFn: (payload: BillingUpdateSettingsPayload) =>
      BillingService.updateBillingSettings(payload),
    onMutate: () => clearToasts(),
    onError: (e, variables) => {
      monitoringExceptionHelper(e, {
        extra: { variables },
      });
      showToast({
        message: t('Failed to update overage budgets'),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const showSavedToast = useCallback(() => {
    showToast({
      message: t('Successfully updated overage budgets'),
      variant: toastVariant.SUCCESS,
    });
  }, [showToast, t]);

  const handleSave = useCallback(async () => {
    if (!allowOverage) {
      await updateBillingSettingsMutation.mutateAsync(noOveragePayload, {
        onSuccess: showSavedToast,
      });
    } else if (allowUnlimitedOverage) {
      await updateBillingSettingsMutation.mutateAsync(unlimitedPayload, {
        onSuccess: showSavedToast,
      });
    } else {
      const payload = mapBudgetKeys((key) => toPayloadValue(budgets[key]));

      if (payload.overall_overage_budget_limit === '0') {
        await updateBillingSettingsMutation.mutateAsync(noOveragePayload, {
          onSuccess: () => {
            setAllowOverage(false);
            showToast({
              message: t(
                'Allow Overage turned off — requires overall budget value greater than $0'
              ),
              variant: toastVariant.WARNING,
              delay: toastDelay.long,
            });
          },
        });
      } else {
        await updateBillingSettingsMutation.mutateAsync(payload, {
          onSuccess: showSavedToast,
        });
      }
    }

    refetch();
  }, [
    allowOverage,
    updateBillingSettingsMutation,
    allowUnlimitedOverage,
    budgets,
    refetch,
    showSavedToast,
    showToast,
    t,
  ]);

  const isDirty = useMemo(() => {
    if (allowOverage !== currentAllowsOverage) {
      return true;
    }

    if (allowUnlimitedOverage !== currentIsUnlimited) {
      return true;
    }

    // Ignore other fields if overage is unlimited, because they get ignored
    if (canHaveUnlimitedBudget) {
      return false;
    }

    return BUDGET_KEYS.some(
      (key) =>
        budgets[key] && Number(budgets[key] || '-1') !== currentBudgets[key]
    );
  }, [
    allowOverage,
    currentAllowsOverage,
    allowUnlimitedOverage,
    currentIsUnlimited,
    budgets,
    currentBudgets,
    canHaveUnlimitedBudget,
  ]);

  // Resource approximations come from the API and are recalculated on Save
  // (via refetch), not while typing. They describe the saved budget, so hide
  // them once the field is edited, when the budget is $0, and when the API
  // has no positive estimate.
  const approximationText = (
    key: BudgetKey,
    approximation: number | null | undefined,
    resource: string
  ) => {
    const budgetValue = Number(budgets[key]) || 0;
    const isUnsaved = budgetValue !== (currentBudgets[key] ?? 0);

    if (!budgetValue || isUnsaved || !approximation) {
      return null;
    }

    return t('approx. {{amount}} additional {{resource}}', {
      amount: Math.round(approximation).toLocaleString(),
      resource,
    });
  };

  return (
    <>
      <div className="flex items-center justify-between relative">
        <Typography variant="header" size="xl" weight="semibold">
          {t('Overage Budgets')}
        </Typography>
        {!readOnly && (
          <div className="absolute right-[5px] top-0">
            <Button
              disabled={
                disabled || updateBillingSettingsMutation.isPending || !isDirty
              }
              onClick={handleSave}
              qa={{ id: 'save-overage-budgets-button' }}
            >
              {t('Save')}
            </Button>
          </div>
        )}
      </div>
      <Spacer size={30} />
      <div className={GRID_CLASS_NAME}>
        <div>
          <Switch
            data-qa="activity-readonly-toggle"
            label={t('Allow Overage?')}
            checked={canAllowOverage}
            textPlacement="regular-top"
            onChange={(ev) => toggleAllowOverage(ev.target.checked)}
            disabled={readOnly}
          />
        </div>
        <div className="col-span-2">
          <Switch
            data-qa="activity-readonly-toggle"
            label={t('Allow Unlimited Overage Budget')}
            labelAdornment={
              <TooltipIcon
                tooltip={t(
                  'Defaulted to on to ensure that there is no interruption of service. If toggled off, there may be periods where application functions will be limited.'
                )}
              />
            }
            checked={canHaveUnlimitedBudget}
            textPlacement="regular-top"
            onChange={(ev) => toggleUnlimitedBudget(ev.target.checked)}
            disabled={readOnly}
          />
        </div>
      </div>
      {showBreakdown ? (
        <>
          <Spacer size={20} />
          <div className={GRID_CLASS_NAME}>
            <BudgetField
              label={
                <LabelWithTooltip
                  label={t('Overall Monthly Budget')}
                  tooltip={t(
                    `Overall Budget is the max amount you'll be charged in total for overage across all categories. Each category can have additional restrictions as well.`
                  )}
                />
              }
              value={budgets.overall_overage_budget_limit}
              onChange={(val) => setBudget('overall_overage_budget_limit', val)}
              disabled={readOnly || disabled}
              qaId="overall-budget-input"
            />
          </div>
          <Spacer size={30} />
          <Typography size="md" weight="semibold">
            {t('Monthly Budgets per Category')}
          </Typography>
          <Spacer size={20} />
          <div className={GRID_CLASS_NAME}>
            <BudgetField
              label={
                <LabelWithTooltip
                  label={t('Kizen AI Credits Overage')}
                  tooltip={t(
                    'Cost of an AI request is only reported after completing it. Your spend may occasionally exceed this budget by up to $10 before we can pause further requests.'
                  )}
                />
              }
              value={budgets.ai_credits_overage_budget_limit}
              onChange={(val) =>
                setBudget('ai_credits_overage_budget_limit', val)
              }
              disabled={readOnly || disabled}
              qaId="ai-credits-budget-input"
              helperText={approximationText(
                'ai_credits_overage_budget_limit',
                usageSummary?.approximations?.ai_credits,
                t('Kizen AI credits')
              )}
            />
            <BudgetField
              label={t('Agentic Workflows Overage')}
              value={budgets.automation_overage_budget_limit}
              onChange={(val) =>
                setBudget('automation_overage_budget_limit', val)
              }
              disabled={readOnly || disabled}
              qaId="agentic-workflows-budget-input"
              helperText={approximationText(
                'automation_overage_budget_limit',
                usageSummary?.approximations?.automations,
                t('steps')
              )}
            />
            <BudgetField
              label={t('Marketing Platform Overage')}
              value={budgets.email_overage_budget_limit}
              onChange={(val) => setBudget('email_overage_budget_limit', val)}
              disabled={readOnly || disabled}
              qaId="marketing-budget-input"
              helperText={approximationText(
                'email_overage_budget_limit',
                usageSummary?.approximations?.emails,
                t('emails')
              )}
            />
            <BudgetField
              label={
                <LabelWithTooltip
                  label={t('Use of External LLMs (via Kizen)')}
                  tooltip={t(
                    'AI providers report the cost of a request after completing it. Your spend may occasionally exceed this budget by up to $10 before we can pause further requests.'
                  )}
                />
              }
              value={budgets.ai_passthrough_overage_budget_limit}
              onChange={(val) =>
                setBudget('ai_passthrough_overage_budget_limit', val)
              }
              disabled={readOnly || disabled}
              qaId="passthrough-budget-input"
            />
          </div>
        </>
      ) : null}
    </>
  );
};
